import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { Settings } from "../../../settings";
import { PixiSettings } from "../../../pixiSettings";

// Static mode configuration - Base game
export const STATIC_CONFIG = {
  widthMultiplier: 1.149,
  heightMultiplier: 1.21,
  verticalOffset: 1,
  horizontalOffset: 0,
};

// Static mode configuration - Feature game (Hold and Win)
export const STATIC_CONFIG_FEATURE = {
  widthMultiplier: 1.149,
  heightMultiplier: 1.215,
  verticalOffset: 0,
  horizontalOffset: 0,
};

// Spine mode configuration
export const SPINE_CONFIG = {
  verticalOffset: -0.002,
  horizontalOffset: -0.001,
  widthMultiplier: 1.2103,
  heightMultiplier: 1.165,
  frameWidthRatio: 1,
  frameHeightRatio: 1,
};

export const LINES_TEXT_CONFIG = {
  fontSize: 22, // Base font size
  fontWeight: "300",
  lineHeight: 1.0, // Multiplier for spacing between letters (1.2 = 120% of font size)
  xOffsetLeft: 6, // Horizontal offset from left slot center
  xOffsetRight: 2, // Horizontal offset from right slot center
  yOffset: 2, // Vertical offset from slot center (universal)
};

// Manages spine animation for ReelFront component
class ReelFrontController {
  constructor(
    container,
    symbolWidth,
    symbolHeight,
    dimensions,
    dividerDimensions,
  ) {
    this.container = container;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dimensions = dimensions;
    this.dividerDimensions = dividerDimensions;
    this.spine = null;

    // Text containers attached to spine slots
    this.linesLeftContainer = null;
    this.linesRightContainer = null;

    // Internal caches for performance optimization
    this._availableAnimationsCache = null;
    this._dividerCountCache = Settings.REEL_COUNT - 1;

    // Defer spine creation to avoid blocking the main thread
    requestAnimationFrame(() => {
      this.initializeSpineAnimation();
    });
  }

  // Initialize spine and play front animations
  initializeSpineAnimation() {
    this.spine = this.createSpine();

    if (!this.spine) {
      return;
    }

    // VALIDATION: Ensure spine has all required components
    const missingComponents = [];
    if (!this.spine.skeleton) missingComponents.push("skeleton");
    if (!this.spine.state) missingComponents.push("state");
    if (!this.spine.skeleton?.data) missingComponents.push("skeleton.data");
    if (!this.spine.skeleton?.slots) missingComponents.push("skeleton.slots");
    if (!this.spine.skeleton?.bones) missingComponents.push("skeleton.bones");

    if (missingComponents.length > 0) {
      return;
    }

    // Setup spine
    this.spine.position.set(0, 0);
    this.spine.zIndex = this.container.zIndex;
    this.spine.visible = true;

    // Add to container
    this.container.addChild(this.spine);

    // VALIDATION: Final check that spine is properly attachead
    if (this.spine.parent !== this.container) {
      return;
    }

    // Invalidate animations cache when spine is reinitialized
    this._availableAnimationsCache = null;

    // Apply scaling to match container dimensions
    this.applySpineScaling();

    // Play all front animations from reel_background spine
    this.playAnimation("reel_front_glow", true);
    this.playAnimation("reel_front_swipe", true, 1);
    this.playAnimation("reel_front", true, 2);

    // Initialize LINES text in slots
    this.initializeLinesText();
  }

  // Initialize LINES text and attach to spine slots
  initializeLinesText() {
    if (!this.spine || !this.spine.skeleton) {
      return;
    }

    // Check if left slot exists
    const leftSlot = this.spine.skeleton.findSlot("txt_slot_lines_left");
    if (leftSlot) {
      this.linesLeftContainer = this.createLinesTextContainer("left");
      this.spine.addSlotObject("txt_slot_lines_left", this.linesLeftContainer);
    }

    // Check if right slot exists
    const rightSlot = this.spine.skeleton.findSlot("txt_slot_lines_right");
    if (rightSlot) {
      this.linesRightContainer = this.createLinesTextContainer("right");
      this.spine.addSlotObject(
        "txt_slot_lines_right",
        this.linesRightContainer,
      );
    }
  }

  // Create a container with vertical "LINES" text
  createLinesTextContainer(side) {
    const container = new PIXI.Container();
    container.name = "lines_text_attached_to_slot";
    container.alpha = 1;

    // Vertical text: each character on a new line
    const text = "L\nI\nN\nE\nS";

    // Create text style using centralized PRIMARY font style
    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const textStyle = new PIXI.TextStyle({
      fontFamily: primaryStyle.fontFamily,
      fontSize: LINES_TEXT_CONFIG.fontSize,
      fontWeight: LINES_TEXT_CONFIG.fontWeight,
      fill: primaryStyle.fill,
      fillGradientType: primaryStyle.fillGradientType,
      stroke: primaryStyle.stroke,
      strokeThickness: primaryStyle.strokeThickness,
      dropShadow: primaryStyle.dropShadow,
      dropShadowColor: primaryStyle.dropShadowColor,
      dropShadowDistance: primaryStyle.dropShadowDistance,
      dropShadowBlur: primaryStyle.dropShadowBlur,
      dropShadowAlpha: primaryStyle.dropShadowAlpha,
      dropShadowAngle: primaryStyle.dropShadowAngle,
      align: "center",
      lineHeight: LINES_TEXT_CONFIG.fontSize * LINES_TEXT_CONFIG.lineHeight,
    });

    const linesText = new PIXI.Text(text, textStyle);
    linesText.resolution = 2;
    linesText.anchor.set(0.5);
    linesText.x = Math.round(
      side === "left"
        ? LINES_TEXT_CONFIG.xOffsetLeft
        : LINES_TEXT_CONFIG.xOffsetRight,
    );
    linesText.y = Math.round(LINES_TEXT_CONFIG.yOffset);

    container.addChild(linesText);

    return container;
  }

  // Create and verify spine instance
  createSpine() {
    try {
      // Check if assets are loaded
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "reel_background-skeleton";
      const atlasAlias = "reel_background-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      // Create independent spine instance
      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      // Verify the spine was created properly
      if (!spine || !spine.skeleton || !spine.state) {
        return null;
      }

      // Verify skeleton has required data
      if (!spine.skeleton.data || !spine.skeleton.data.animations) {
        return null;
      }

      return spine;
    } catch (error) {
      return null;
    }
  }

  // Play animation by name on specified track
  playAnimation(animationName, loop = false, track = 0) {
    if (!this.spine || !this.spine.state) {
      return;
    }

    const animations = this.spine.skeleton.data.animations;
    const animation = animations.find((anim) => anim.name === animationName);

    if (!animation) {
      return;
    }

    this.spine.state.setAnimation(track, animationName, loop);
  }

  // Scale spine to match reel area dimensions
  applySpineScaling() {
    if (!this.spine || !this.symbolHeight || !this.dimensions) {
      return;
    }

    const spineBounds = this.spine.getLocalBounds();
    if (!spineBounds || spineBounds.width <= 0 || spineBounds.height <= 0) {
      requestAnimationFrame(() => {
        this.applySpineScaling();
      });
      return;
    }

    // Target inner frame size that should align with the reel area
    // Use cached divider count
    const dividerWidth = this.dividerDimensions?.width ?? 0;
    // Intentionally reduce by two divider widths (overflow on the sides)
    const targetFrameWidth =
      this.symbolWidth * Settings.REEL_COUNT * SPINE_CONFIG.widthMultiplier +
      dividerWidth * (this._dividerCountCache - 2);
    const targetFrameHeight =
      this.symbolHeight *
      Settings.SYMBOLS_VISIBLE *
      SPINE_CONFIG.heightMultiplier;

    // Artwork-specific frame ratios inside the spine bounds
    const cfg = SPINE_CONFIG;
    const requiredSpineWidth = targetFrameWidth / (cfg.frameWidthRatio || 1);
    const requiredSpineHeight = targetFrameHeight / (cfg.frameHeightRatio || 1);

    const scaleX = requiredSpineWidth / spineBounds.width;
    const scaleY = requiredSpineHeight / spineBounds.height;
    this.spine.scale.set(scaleX, scaleY);

    this.spine.position.set(0, 0);
  }

  // Update dimensions and reapply scaling
  resizeReelFront(
    newSymbolWidth,
    newSymbolHeight,
    newDimensions,
    newDividerDimensions,
  ) {
    this.symbolWidth = newSymbolWidth;
    this.symbolHeight = newSymbolHeight;
    if (newDimensions) {
      this.dimensions = newDimensions;
    }
    if (newDividerDimensions) {
      this.dividerDimensions = newDividerDimensions;
    }
    if (this.spine) {
      this.applySpineScaling();
    }
  }

  // Get list of available animation names
  // Cached for performance - invalidated when spine is reinitialized
  getAvailableAnimations() {
    if (!this.spine?.skeleton?.data?.animations) {
      return [];
    }

    // Check cache validity
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    // Calculate and cache
    const animations = this.spine.skeleton.data.animations.map(
      (anim) => anim.name,
    );
    this._availableAnimationsCache = animations;

    return animations;
  }
}

export default ReelFrontController;
